import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 25 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const About = () => {
  const [c, setC] = useState(null);

  useEffect(() => {
    axios.get(`${API}/page-content/about`).then(r => setC(r.data)).catch(console.error);
  }, []);

  const timelineData = c?.timeline || [
    { period: 'Early Years & Training', title: 'Foundation in Art', content: 'Inherited artistic talent from his father Sri Gangadhar, a celebrated professional artist and film publicity designer. First collaboration: Telugu movie Jyothi. Diploma in Fine Arts (1984) and Post Diploma (1985) from Govt College of Arts & Crafts, Madras.' },
    { period: 'The Film Era', title: 'Cinema Publicity Artist', content: 'Contributed to publicity design of 900+ movies in Kannada, Telugu, and Tamil cinema. Worked with Dr. Rajkumar, Dr. Vishnuvardhan, Kamal Haasan, and Chiranjeevi.' },
    { period: 'The Ashram Years', title: 'Spiritual Devotion', content: "Created devotional works for Sri Ganapathi Sachchidananda Swamiji's ashram including Sri Padha Vallabha and Sri Narasimha Saraswathi life history paintings. Exhibited 60 oil paintings for Swamiji's 60th birthday in Mysore." },
    { period: 'Temple Masterpieces', title: 'Monumental Works', content: 'Nava Nandi Series at Mahanandi Temple. 46 Lord Hanuman life history oil paintings (5x8 ft) at Karya Siddhi Hanuman Temple, Dallas. Lord Rama wall murals at Sri Ganapathi Sachchidananda Ashrama, Ayodhya.' }
  ];

  const achievements = c?.achievements || [
    "Titled 'Datta Peetha Asthan Chitrakara' by Sri Sri Sri Ganapathi Sachchidananda Swamiji",
    'Creator of 46 Lord Hanuman life history oil paintings at Karya Siddhi Hanuman Temple, Dallas',
    'Publicity design for 900+ movies in Kannada, Telugu, and Tamil cinema',
    'Gold medal from Loyola College, Chennai',
    'Awards from A.P. Balala Academy and Tamil Nadu Forest Department',
  ];

  const bioParagraphs = c?.bio_paragraphs || [
    'Gangadhar Som inherited his artistic talent from his father, Sri Gangadhar, a celebrated professional artist and film publicity designer. He began his artistic journey at a young age, working alongside his father on film projects. Their first collaboration was the Telugu movie Jyothi.',
    'He earned a Diploma in Fine Arts in 1984 and a Post Diploma in 1985 from the Government College of Arts & Crafts, Madras. A master of various artistic mediums including oil paintings, acrylics, watercolors, spray paintings, and digital illustrations.',
    'With over 40 years of experience, he has contributed to the publicity design of more than 900 movies across the Kannada, Telugu, and Tamil film industries, working with legendary actors such as Dr. Rajkumar, Dr. Vishnuvardhan, Kamal Haasan, and Chiranjeevi.'
  ];

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="about-page">
      <SEO
        title="About Gangadhar Som - Renowned Artist | 40+ Years in Fine Arts & Cinema"
        description="Learn about Gangadhar Som's artistic journey — from South Indian cinema publicity design (900+ movies) to monumental temple paintings including 46 Lord Hanuman life history paintings at Dallas. Master of oil, acrylics, watercolors, and digital art."
        url={`${window.location.origin}/about`}
      />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="mb-16 sm:mb-24">
          <motion.span variants={fadeUp} className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">
            {c?.page_label || 'About the Artist'}
          </motion.span>
          <motion.h1 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light tracking-tight mt-3 mb-6" data-testid="about-page-title">
            {c?.page_title || 'Gangadhar Som'}
          </motion.h1>
          <motion.p variants={fadeUp} className="font-sans text-sm sm:text-base md:text-lg leading-relaxed text-foreground/80 max-w-3xl">
            {c?.page_subtitle || 'A renowned professional artist with 40+ years in fine arts and South Indian cinema. Creator of the 46 Lord Hanuman life history paintings at Karya Siddhi Hanuman Temple, Dallas, Texas.'}
          </motion.p>
        </motion.div>

        {/* Portrait and Bio */}
        <motion.div
          initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.7 }}
          className="grid md:grid-cols-5 gap-8 sm:gap-12 mb-20 sm:mb-32"
        >
          <div className="md:col-span-2">
            <div className="overflow-hidden rounded-lg">
              <img
                src={c?.portrait_image || "https://static.wixstatic.com/media/d54d7c_60c0f08a40ae4e48b071a852b4de9075~mv2.jpg"}
                alt={c?.page_title || "Gangadhar Som"} className="w-full h-auto transition-transform duration-700 hover:scale-105"
                data-testid="artist-portrait"
              />
            </div>
          </div>
          <div className="md:col-span-3 space-y-4 sm:space-y-6">
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light">{c?.bio_title || 'Biography'}</h2>
            <div className="space-y-3 sm:space-y-4 font-sans text-sm sm:text-base leading-relaxed text-foreground/80">
              {bioParagraphs.map((para, i) => <p key={i}>{para}</p>)}
            </div>
          </div>
        </motion.div>

        {/* Timeline */}
        {timelineData.length > 0 && (
          <div className="mb-20 sm:mb-32">
            <motion.h2
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} className="font-serif text-2xl sm:text-3xl md:text-5xl font-light tracking-tight mb-10 sm:mb-16"
            >
              {c?.journey_title || 'Artistic Journey'}
            </motion.h2>
            <div className="space-y-10 sm:space-y-16 relative">
              <div className="hidden md:block absolute left-[24.5%] top-0 bottom-0 w-px bg-border" />
              {timelineData.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                  className="grid md:grid-cols-4 gap-4 sm:gap-6 md:gap-12"
                  data-testid={`timeline-item-${index}`}
                >
                  <div className="md:col-span-1 md:text-right">
                    <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-accent">{item.period}</span>
                  </div>
                  <div className="md:col-span-3">
                    <h3 className="font-serif text-xl sm:text-2xl md:text-3xl font-light mb-3">{item.title}</h3>
                    <p className="font-sans text-sm sm:text-base leading-relaxed text-foreground/80">{item.content}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Achievements */}
        {achievements.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="bg-card rounded-lg p-6 sm:p-8 md:p-12"
          >
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light tracking-tight mb-6 sm:mb-8">
              {c?.achievements_title || 'Achievements & Recognition'}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              {achievements.map((a, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-3" data-testid={`achievement-${i}`}>
                  <div className="w-2 h-2 bg-accent rounded-full mt-1.5 flex-shrink-0" />
                  <p className="font-sans text-sm sm:text-base leading-relaxed text-foreground/80">{a}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Quote */}
        {(c?.quote_text || true) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="mt-20 sm:mt-32 text-center max-w-4xl mx-auto px-4"
          >
            <blockquote className="font-serif text-xl sm:text-2xl md:text-3xl font-light italic text-foreground/90 mb-4 sm:mb-6">
              "{c?.quote_text || 'Swamiji is the God in my life, I have thrived, and my art has flourished due to his generosity.'}"
            </blockquote>
            <cite className="font-sans text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground not-italic">
              — {c?.quote_attribution || 'Gangadhar Som'}
            </cite>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default About;
